import React, { useState, useCallback, useEffect, useRef } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useSocial } from "../../contexts/SocialContext";
import * as api from "../../services/api";
import { Check, ChevronRight, Flame, Link2, Loader2, Search, UserPlus, X } from "lucide-react";
import DetailSheet from "../session/generate/DetailSheet";
import { DeleteConfirm, Group } from "../profile/ProfileParts";
import Avatar from "./Avatar";
import InviteDialog from "./InviteDialog";
import WeeklyLeaderboard from "./WeeklyLeaderboard";
import { MateBadges, useBadgesShown } from "./Badges";
import { LevelChip } from "./LevelShield";
import PartnerCard from "./PartnerCard";

const personRow = "grid w-full items-center gap-2.5 px-3 py-2.5 text-left";

export default function FriendsTab() {
  const {
    friends,
    friendRequests,
    sentRequests,
    friendsLoading,
    acceptRequest,
    rejectRequest,
    removeFriend,
    streaks,
  } = useSocial();
  const badgesShown = useBadgesShown();
  // The server only sends a mate's level while they have badges on too.
  const levelOf = (id) => streaks?.friends?.find((f) => Number(f.id) === Number(id))?.level ?? null;

  const [actionLoading, setActionLoading] = useState(null);
  const [findOpen, setFindOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [openMate, setOpenMate] = useState(null);

  const handleAction = useCallback(async (action, id) => {
    setActionLoading(id);
    try {
      await action(id);
    } catch (err) {
      console.error("Action failed:", err);
    } finally {
      setActionLoading(null);
    }
  }, []);

  return (
    <div className="mt-4 grid grid-cols-[minmax(0,1fr)] gap-5">
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={() => setFindOpen(true)}>
          <Search className="mr-2 h-4 w-4" /> Find mates
        </Button>
        {/* Search only finds people already on Khronos; this is how you
            reach everyone else. */}
        <Button variant="outline" onClick={() => setInviteOpen(true)}>
          <Link2 className="mr-2 h-4 w-4" /> Invite by link
        </Button>
      </div>

      <FindMatesSheet
        open={findOpen}
        onOpenChange={setFindOpen}
        onInvite={() => {
          setFindOpen(false);
          setInviteOpen(true);
        }}
      />
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} kind="app" />

      {friendRequests.length > 0 && (
        <Group title="Requests">
          {friendRequests.map((req) => (
            <div key={req.friendship_id} className={`${personRow} grid-cols-[2rem_minmax(0,1fr)_auto]`}>
              <Avatar name={req.name} photo={req.photo_url} />
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{req.name}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">Wants to be mates</span>
              </span>
              <span className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground"
                  disabled={actionLoading === req.friendship_id}
                  onClick={() => handleAction(rejectRequest, req.friendship_id)}
                >
                  Decline
                </Button>
                <Button
                  size="sm"
                  disabled={actionLoading === req.friendship_id}
                  onClick={() => handleAction(acceptRequest, req.friendship_id)}
                >
                  Accept
                </Button>
              </span>
            </div>
          ))}
        </Group>
      )}

      <PartnerCard />

      <WeeklyLeaderboard />


      <Group title={friends.length ? `Mates (${friends.length})` : "Mates"}>
        {friendsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : friends.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            No mates yet. Find people already on Khronos, or send anyone a link.
          </p>
        ) : (
          friends.map((friend) => (
            <button
              key={friend.friendship_id}
              type="button"
              onClick={() => setOpenMate(friend)}
              className={`${personRow} grid-cols-[2rem_minmax(0,1fr)_1rem] transition-colors hover:bg-muted/30`}
            >
              <Avatar name={friend.name} photo={friend.photo_url} />
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate text-sm font-medium">{friend.name}</span>
                {/* Their consistency level, when you both have badges on. */}
                {badgesShown && <LevelChip level={levelOf(friend.id)} />}
              </span>
              <ChevronRight className="h-4 w-4 text-muted-foreground/70" />
            </button>
          ))
        )}
      </Group>

      {sentRequests.length > 0 && (
        <Group title="Waiting on">
          {sentRequests.map((req) => (
            <div key={req.friendship_id} className={`${personRow} grid-cols-[2rem_minmax(0,1fr)_auto]`}>
              <Avatar name={req.name} photo={req.photo_url} />
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{req.name}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">Request sent</span>
              </span>
              {/* Deleting a pending friendship withdraws it. */}
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground"
                disabled={actionLoading === req.friendship_id}
                onClick={() => handleAction(removeFriend, req.friendship_id)}
              >
                Cancel
              </Button>
            </div>
          ))}
        </Group>
      )}

      <MateSheet mate={openMate} onClose={() => setOpenMate(null)} />
    </div>
  );
}

/** Search for people already on Khronos and send them a request. */
function FindMatesSheet({ open, onOpenChange, onInvite }) {
  const { friends, friendRequests, sentRequests, sendRequest, acceptRequest } = useSocial();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // The query the results are for, so "nobody matches" never flashes for a
  // query that's still on its way.
  const [resultsFor, setResultsFor] = useState("");
  const [sending, setSending] = useState(null);
  const inputRef = useRef(null);
  const q = query.trim();

  useEffect(() => {
    if (open) return;
    setQuery("");
    setResults([]);
    setResultsFor("");
  }, [open]);

  useEffect(() => {
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      api
        .searchUsers(q)
        .then((data) => {
          if (cancelled) return;
          setResults(data);
          setResultsFor(q);
        })
        .catch((err) => console.error("Search failed:", err))
        .finally(() => !cancelled && setSearching(false));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [q]);

  const run = async (id, action) => {
    setSending(id);
    try {
      await action();
    } catch (err) {
      console.error("Mate request failed:", err);
    } finally {
      setSending(null);
    }
  };

  const isFriend = (id) => friends.some((f) => f.id === id);
  const hasSent = (id) => sentRequests.some((r) => r.id === id);
  const theirRequest = (id) => friendRequests.find((r) => r.id === id);

  const clear = () => {
    setQuery("");
    inputRef.current?.focus();
  };

  const status = (u) => {
    if (isFriend(u.id)) {
      return (
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Check className="h-3.5 w-3.5" /> Mates
        </span>
      );
    }
    if (hasSent(u.id)) return <span className="text-xs text-muted-foreground">Request sent</span>;
    const req = theirRequest(u.id);
    const busy = sending === u.id;
    const spinner = <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />;
    // They've already asked you: accept it rather than send one back.
    if (req) {
      return (
        <Button size="sm" disabled={busy} onClick={() => run(u.id, () => acceptRequest(req.friendship_id))}>
          {busy ? spinner : <Check className="mr-1.5 h-3.5 w-3.5" />}
          Accept
        </Button>
      );
    }
    return (
      <Button size="sm" variant="secondary" disabled={busy} onClick={() => run(u.id, () => sendRequest(u.id))}>
        {busy ? spinner : <UserPlus className="mr-1.5 h-3.5 w-3.5" />}
        Add
      </Button>
    );
  };

  return (
    <DetailSheet open={open} onOpenChange={onOpenChange} title="Find mates" fill>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          aria-label="Search by name or email"
          placeholder="Search by name or email"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          // Search on the keyboard puts it away, so the results get the screen.
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          enterKeyHint="search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          className="pl-9 pr-10"
          autoFocus
        />
        {searching ? (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        ) : (
          query && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={clear}
              className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )
        )}
      </div>

      {q.length < 2 ? (
        <p className="text-sm leading-snug text-muted-foreground">
          Type at least two letters of their name or email. This finds people already on Khronos.
        </p>
      ) : results.length > 0 ? (
        <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-background">
          {results.map((u) => (
            <div key={u.id} className={`${personRow} grid-cols-[2rem_minmax(0,1fr)_auto]`}>
              <Avatar name={u.name} photo={u.photo_url} />
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{u.name}</span>
                {u.email && <span className="mt-0.5 block truncate text-xs text-muted-foreground">{u.email}</span>}
              </span>
              {status(u)}
            </div>
          ))}
        </div>
      ) : (
        resultsFor === q && (
          <p className="text-center text-sm text-muted-foreground">
            Nobody on Khronos matches “{q}”.
          </p>
        )
      )}

      {/* Search only reaches people already signed up; the link reaches anyone. */}
      <div className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-border px-3 py-2.5">
        <span className="text-sm text-muted-foreground">Not on Khronos yet?</span>
        <Button size="sm" variant="outline" onClick={onInvite}>
          <Link2 className="mr-1.5 h-3.5 w-3.5" /> Invite by link
        </Button>
      </div>
    </DetailSheet>
  );
}

/** One mate: their streak and badges, and removing them, which asks first. */
function MateSheet({ mate, onClose }) {
  const { streaks, removeFriend } = useSocial();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(mate);
  useEffect(() => {
    if (mate) setLast(mate);
    else setConfirming(false);
  }, [mate]);
  const m = mate || last;
  if (!m) return null;

  const streak = streaks.friends.find((f) => Number(f.id) === Number(m.id))?.current_streak || 0;

  const handleRemove = async () => {
    setBusy(true);
    try {
      await removeFriend(m.friendship_id);
      onClose();
    } catch (err) {
      console.error("Remove mate failed:", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DetailSheet open={Boolean(mate)} onOpenChange={(o) => !o && onClose()} title={m.name}>
      <div className="flex items-center gap-3">
        <Avatar name={m.name} photo={m.photo_url} className="h-12 w-12 text-base" />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{m.name}</p>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
            <Flame className="h-3.5 w-3.5 text-orange-400" />
            {streak ? `${streak}-day streak` : "No streak running"}
          </p>
        </div>
      </div>
      <MateBadges mateId={m.id} />
      <DeleteConfirm
        action="Remove"
        name={`${m.name} as a mate`}
        consequence="You'll stop seeing each other's workouts. You can send a new request later."
        confirming={confirming}
        onAsk={() => setConfirming(true)}
        onCancel={() => setConfirming(false)}
        onConfirm={handleRemove}
        busy={busy}
      />
    </DetailSheet>
  );
}
